// frontend/src/components/WorkflowActions.js
import {
  Lightning, Truck, Microscope, Receipt,
  ShoppingCart,
} from '@phosphor-icons/react';
import { writeHandoff } from '../lib/workflowState';

// ── Action Config ────────────────────────────────────────────────────────────
// Each entry defines one button. To add a new workflow action, add one entry here.
//
// Fields:
//   label         string      Button text
//   icon          Component   Phosphor icon
//   color         string      hsl() accent color for this action
//   targetPath    string      Route to open, e.g. '/factory'
//   requiredStatus string[]   Record statuses that show this button
//   requiredRoles  string[]   Roles that can see/use this button
//   buildPayload  function    (record) => object  Pre-fill data written to localStorage

const ACTIONS_CONFIG = {
  order: [
    {
      label: 'CREATE WORK ORDER',
      icon: Lightning,
      color: 'hsl(200,80%,55%)',
      targetPath: '/factory',
      requiredStatus: ['confirmed'],
      requiredRoles: ['admin', 'factory_manager'],
      buildPayload: (r) => ({
        order_id: r.id,
        order_no: r.order_no,
        product_id: r.product_id,
        product_name: r.product_name,
        product_sku: r.product_sku,
        planned_qty: r.quantity,
        customer_name: r.customer_name,
      }),
    },
    {
      label: 'SCHEDULE DISPATCH',
      icon: Truck,
      color: 'hsl(12,90%,55%)',
      targetPath: '/dispatch',
      requiredStatus: ['ready_to_dispatch'],
      requiredRoles: ['admin', 'dispatch_user', 'sales_manager'],
      buildPayload: (r) => ({
        order_id: r.id,
        order_no: r.order_no,
        customer_name: r.customer_name,
        product_name: r.product_name,
        product_sku: r.product_sku,
        quantity: r.quantity,
      }),
    },
  ],

  work_order: [
    {
      label: 'CREATE QC RECORD',
      icon: Microscope,
      color: 'hsl(142,70%,50%)',
      targetPath: '/quality',
      requiredStatus: ['completed'],
      requiredRoles: ['admin', 'quality_manager'],
      buildPayload: (r) => ({
        wo_id: r.id,
        wo_no: r.wo_no,
        product_id: r.product_id,
        product_name: r.product_name,
        order_no: r.order_no,
        planned_qty: r.planned_qty,
      }),
    },
  ],

  qc_record: [
    {
      label: 'CREATE DISPATCH NOTE',
      icon: Truck,
      color: 'hsl(12,90%,55%)',
      targetPath: '/dispatch',
      requiredStatus: ['pass'],
      requiredRoles: ['admin', 'dispatch_user'],
      buildPayload: (r) => ({
        qc_id: r.id,
        order_no: r.order_no,
        product_name: r.product_name,
        product_sku: r.product_sku,
        customer_name: r.customer_name,
        quantity: r.planned_qty,
        qc_released: true,
      }),
    },
  ],

  dispatch_note: [
    {
      label: 'CREATE INVOICE',
      icon: Receipt,
      color: 'hsl(280,65%,60%)',
      targetPath: '/finance/invoices/new',
      requiredStatus: ['dispatched'],
      requiredRoles: ['admin', 'finance_user', 'sales_manager'],
      buildPayload: (r) => ({
        dispatch_id: r.id,
        dispatch_no: r.dispatch_no,
        order_no: r.order_no,
        customer_name: r.customer_name,
        customer_id: r.customer_id,
        product_name: r.product_name,
        quantity: r.quantity,
        invoice_amount: r.invoice_amount,
      }),
    },
  ],

  customer: [
    {
      label: 'CREATE ORDER',
      icon: ShoppingCart,
      color: 'hsl(75,100%,50%)',
      targetPath: '/orders',
      requiredStatus: ['active', 'Active', undefined, null, ''],
      requiredRoles: ['admin', 'sales_manager'],
      buildPayload: (r) => ({
        customer_id: r.id,
        customer_name: r.name,
        currency: r.currency || 'INR',
        payment_terms: r.payment_terms,
      }),
    },
  ],

  product: [
    {
      label: 'CREATE ORDER',
      icon: ShoppingCart,
      color: 'hsl(75,100%,50%)',
      targetPath: '/orders',
      requiredStatus: ['active', 'Active', undefined, null, ''],
      requiredRoles: ['admin', 'sales_manager'],
      buildPayload: (r) => ({
        product_id: r.id,
        product_name: r.name,
        product_sku: r.sku,
        unit_price: r.selling_price,
      }),
    },
  ],
};

// ── Component ────────────────────────────────────────────────────────────────

const BTN = (color) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '4px 10px', cursor: 'pointer',
  background: 'transparent',
  border: `1px solid ${color}44`,
  color, fontSize: 9,
  fontFamily: "'JetBrains Mono', monospace",
  textTransform: 'uppercase', letterSpacing: '0.08em',
  transition: 'background 0.15s',
});

/**
 * WorkflowActions — renders status-gated action buttons for a record.
 *
 * Props:
 *   recordType  string   Key in ACTIONS_CONFIG: 'order' | 'work_order' | 'qc_record' | 'dispatch_note' | 'customer' | 'product'
 *   record      object   The full record object (must have .status and .id)
 *   userRole    string   Current user's role
 */
export default function WorkflowActions({ recordType, record, userRole }) {
  const actions = ACTIONS_CONFIG[recordType] || [];

  const visible = actions.filter(action => {
    const statusOk = action.requiredStatus.includes(record.status);
    const roleOk = action.requiredRoles.includes(userRole);
    return statusOk && roleOk;
  });

  if (visible.length === 0) return null;

  const handleClick = (action) => {
    const payload = action.buildPayload(record);
    const key = writeHandoff(payload);
    window.open(`${action.targetPath}?action_key=${key}`, '_blank');
  };

  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }} data-testid="workflow-actions">
      {visible.map(action => {
        const Icon = action.icon;
        return (
          <button
            key={action.label}
            style={BTN(action.color)}
            onClick={() => handleClick(action)}
            onMouseEnter={e => e.currentTarget.style.background = action.color + '18'}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            data-testid={`action-btn-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <Icon size={10} />
            {action.label}
            <span style={{ fontSize: 8, opacity: 0.5 }}>↗</span>
          </button>
        );
      })}
    </div>
  );
}
